import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { OnboardingProvider } from "@/contexts/OnboardingContext";
import { ThemeProvider } from "@/contexts/ThemeContext";
import { useState, useEffect, lazy, Suspense } from "react";
import { OnboardingModal } from "@/components/onboarding/OnboardingModal";
import { GuidedTour } from "@/components/onboarding/GuidedTour";
import { CommandPalette } from "@/components/search/CommandPalette";
import { useOrganization } from "@/hooks/useOrganization";
import '@/i18n';

// Eager load critical pages
import Auth from "./pages/Auth";
import Landing from "./pages/Landing";
import NotFound from "./pages/NotFound";

// Lazy load non-critical pages for code splitting
const Dashboard = lazy(() => import("./pages/Dashboard"));
const Chat = lazy(() => import("./pages/Chat"));
const Tasks = lazy(() => import("./pages/Tasks"));
const Team = lazy(() => import("./pages/Team"));
const Approvals = lazy(() => import("./pages/Approvals"));
const Settings = lazy(() => import("./pages/Settings"));
const ActivityLog = lazy(() => import("./pages/ActivityLog"));
const Calendar = lazy(() => import("./pages/Calendar"));
const Workload = lazy(() => import("./pages/Workload"));
const AuditLog = lazy(() => import("./pages/AuditLog"));
const SystemLogs = lazy(() => import("./pages/SystemLogs"));
const IntegrationDocs = lazy(() => import("./pages/IntegrationDocs"));

const queryClient = new QueryClient();

// Loading fallback for lazy-loaded routes
function PageLoader() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-background">
      <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
    </div>
  );
}

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { user, loading } = useAuth();
  
  if (loading) {
    return <PageLoader />;
  }
  
  if (!user) {
    return <Navigate to="/auth" replace />;
  }
  
  return <>{children}</>;
}

function OnboardingWrapper({ children }: { children: React.ReactNode }) {
  const { needsOnboarding, loading, refetch } = useOrganization();
  const [showOnboarding, setShowOnboarding] = useState(false);

  useEffect(() => {
    if (!loading && needsOnboarding) {
      setShowOnboarding(true);
    }
  }, [loading, needsOnboarding]);

  const handleOnboardingComplete = () => {
    setShowOnboarding(false);
    refetch();
  };

  return (
    <>
      {children}
      <OnboardingModal open={showOnboarding} onComplete={handleOnboardingComplete} />
      <GuidedTour />
      <CommandPalette />
    </>
  );
}

function AppRoutes() {
  return (
    <Suspense fallback={<PageLoader />}>
      <Routes>
        <Route path="/" element={<Landing />} />
        <Route path="/auth" element={<Auth />} />
        <Route path="/dashboard" element={<ProtectedRoute><OnboardingWrapper><Dashboard /></OnboardingWrapper></ProtectedRoute>} />
        <Route path="/chat" element={<ProtectedRoute><OnboardingWrapper><Chat /></OnboardingWrapper></ProtectedRoute>} />
        <Route path="/tasks" element={<ProtectedRoute><OnboardingWrapper><Tasks /></OnboardingWrapper></ProtectedRoute>} />
        <Route path="/calendar" element={<ProtectedRoute><OnboardingWrapper><Calendar /></OnboardingWrapper></ProtectedRoute>} />
        <Route path="/workload" element={<ProtectedRoute><OnboardingWrapper><Workload /></OnboardingWrapper></ProtectedRoute>} />
        <Route path="/team" element={<ProtectedRoute><OnboardingWrapper><Team /></OnboardingWrapper></ProtectedRoute>} />
        <Route path="/approvals" element={<ProtectedRoute><OnboardingWrapper><Approvals /></OnboardingWrapper></ProtectedRoute>} />
        <Route path="/activity" element={<ProtectedRoute><OnboardingWrapper><ActivityLog /></OnboardingWrapper></ProtectedRoute>} />
        <Route path="/audit" element={<ProtectedRoute><OnboardingWrapper><AuditLog /></OnboardingWrapper></ProtectedRoute>} />
        <Route path="/system-logs" element={<ProtectedRoute><OnboardingWrapper><SystemLogs /></OnboardingWrapper></ProtectedRoute>} />
        <Route path="/integrations" element={<ProtectedRoute><OnboardingWrapper><IntegrationDocs /></OnboardingWrapper></ProtectedRoute>} />
        <Route path="/settings" element={<ProtectedRoute><OnboardingWrapper><Settings /></OnboardingWrapper></ProtectedRoute>} />
        <Route path="*" element={<NotFound />} />
      </Routes>
    </Suspense>
  );
}

const App = () => (
  <QueryClientProvider client={queryClient}>
    <ThemeProvider>
      <AuthProvider>
        <OnboardingProvider>
          <TooltipProvider>
            <Toaster />
            <Sonner />
            <BrowserRouter>
              <AppRoutes />
            </BrowserRouter>
          </TooltipProvider>
        </OnboardingProvider>
      </AuthProvider>
    </ThemeProvider>
  </QueryClientProvider>
);

export default App;
